import { useState, type FormEvent, type RefObject } from 'react';
import {
  useLooks, usePatchLook, useDeleteLook, usePublishLook, useUnpublishLook, type LookDto,
} from '../api/looks';
import { usePutOutfit, type EquippedSlot } from '../api/wardrobe';
import { useAppeal, reasonLabel } from '../api/moderation';
import { ApiClientError } from '../api/client';
import { LookPicture } from './LookPicture';
import { validateName } from '../companion/nameValidation';
import { deriveFormError } from '../screens/formErrors';
import { BottomSheet, Dialog } from '../components/BottomSheet';

export type LooksSheetProps = {
  companionId: number;
  onClose: () => void;
  /** CRITICAL fix (reviewer finding): fired with the applied Look's own slots after a successful
   *  apply, IN ADDITION TO the "Look applied" toast this component already shows itself. This
   *  sheet has no idea whether it's hosted inside an open `WardrobeScreen` with its own local
   *  draft store (`useOutfitDraft`) that also needs rebaselining — invalidating the `wardrobe`
   *  query (which `usePutOutfit` already does) is enough on its own when hosted from Habitat
   *  (no draft store exists there), but NOT enough inside WardrobeScreen: that screen's draft
   *  store is the thing every subsequent item tap actually reads/writes, and it is NOT reset by
   *  a wardrobe-query invalidation. Left undefined from Habitat's `CompanionCard`. */
  onApplied?: (slots: Record<string, EquippedSlot>) => void;
  /** The button that opened this sheet — see BottomSheet.tsx's own doc comment on why this is
   *  needed for reliable focus-return (this app's sheets are all externally controlled, not
   *  built on `Dialog.Trigger`). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
  variant?: 'sheet' | 'overlay';
  /** Wardrobe-only: what the empty state's own action does (finding 11). Undefined from Habitat,
   *  where there is no draft to save and the sentence is simply true as written. */
  onSaveCurrent?: () => void;
};

const THUMB_SIZE = 256;

// None of this sheet's mutations are optimistic (unlike usePutOutfit, whose own applied-Look
// commit below IS) — the list only ever reflects what the server actually holds, so SCREENS.md's
// "rollback changes" for a failed rename/publish/unpublish/delete is naturally a no-op here.
// Publish/unpublish/delete failures show this generic toast; rename failures instead go through
// `deriveFormError` to an inline field error under the name input (IMPORTANT fix, reviewer
// finding) — a server 400 "Invalid field: name" read better next to the field it's about than as
// unmapped raw text in a toast, matching SaveLookSheet's own rename-adjacent form error pattern.
function friendlyError(err: unknown): string {
  if (err instanceof ApiClientError && err.message) return err.message;
  return 'Something went wrong — please try again.';
}

/**
 * SCREENS.md "Looks (sheet within Wardrobe + section on Habitat companion row)" — one shared
 * sheet, opened from either entry point (WardrobeScreen's preview controls, or a Habitat
 * CompanionCard's own "Looks" action) with the same `companionId` prop either way.
 *
 * Every saved Look: a 256 CSS px composite thumb (reserved width/height, `alt` = the Look's own
 * name), the name, a "Published" marker and a "Profile" marker when applicable — both a text
 * label, not a colour alone (§11.8) — and four actions: tap the thumb to apply it to this
 * companion (immediate commit via `usePutOutfit`, same semantics as an equip/unequip — see
 * `WardrobeScreen.commitDraft`'s own doc comment), rename inline, toggle profile display, and
 * publish/unpublish — the last two, plus delete, all go through the same two-tap confirm
 * affordance `SaveLookSheet`'s cap view already established (no modal-confirm primitive exists
 * in this app).
 *
 * Built on `BottomSheet` (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3).
 */
export function LooksSheet({
  companionId, onClose, onApplied, restoreFocusRef, portalContainer, variant = 'sheet', onSaveCurrent,
}: LooksSheetProps) {
  const looksQuery = useLooks(companionId);
  const putOutfit = usePutOutfit(companionId);
  const patchLook = usePatchLook();
  const deleteLook = useDeleteLook();
  const publishLook = usePublishLook();
  const unpublishLook = useUnpublishLook();
  const appeal = useAppeal();

  const [toast, setToast] = useState<string | null>(null);
  // Task 19 (R4, "owner sees own content flagged, honest, never silent"): `LookDto` (server) has
  // no `appealState` field at all (unlike `ModerationNoticeDto`, which guilds/stalls/profiles all
  // carry) — verified against the real record source. Session-local tracking of which look ids
  // have had an appeal successfully submitted is the honest minimum this DTO shape supports, so
  // the Appeal button flips to a disabled "Appeal requested" for the rest of this sheet's open
  // lifetime rather than either lying (pretending to read a field that doesn't exist) or letting a
  // second identical appeal submit silently (harmless server-side, but a confusing repeat tap).
  const [appealedIds, setAppealedIds] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editTouched, setEditTouched] = useState(false);
  // IMPORTANT fix (reviewer finding): a server rejection on rename must map to an inline field
  // error under the name input via `deriveFormError`, same as SaveLookSheet — not the generic
  // toast (which used to show the server's raw message unmapped). Captured explicitly here
  // rather than read off `patchLook.error` directly, since `patchLook` is the SAME mutation
  // instance `toggleProfileDisplay` below also uses — reading its `.error` directly would leak a
  // profile-display failure into the rename form, or vice versa.
  const [renameServerError, setRenameServerError] = useState<unknown>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmPublishId, setConfirmPublishId] = useState<number | null>(null);

  const looks = looksQuery.data ?? [];
  const localNameError = editTouched ? validateName(editValue) : undefined;
  const { fieldErrors: renameFieldErrors, formError: renameFormError } = deriveFormError(renameServerError);
  const nameError = localNameError ?? renameFieldErrors.name;

  function startRename(look: LookDto) {
    setEditingId(look.id);
    setEditValue(look.name);
    setEditTouched(false);
    setRenameServerError(null);
  }

  function cancelRename() {
    setEditingId(null);
    setEditValue('');
    setEditTouched(false);
    setRenameServerError(null);
  }

  async function submitRename(e: FormEvent, look: LookDto) {
    e.preventDefault();
    setEditTouched(true);
    setRenameServerError(null);
    if (validateName(editValue)) return;
    try {
      await patchLook.mutateAsync({ id: look.id, name: editValue });
      cancelRename();
    } catch (err) {
      setRenameServerError(err);
    }
  }

  async function handleApply(look: LookDto) {
    try {
      await putOutfit.mutateAsync(look.slots);
      setToast('Look applied');
      onApplied?.(look.slots);
    } catch (err) {
      setToast(friendlyError(err));
    }
  }

  async function toggleProfileDisplay(look: LookDto) {
    try {
      await patchLook.mutateAsync({ id: look.id, isProfileDisplay: !look.isProfileDisplay });
    } catch (err) {
      setToast(friendlyError(err));
    }
  }

  async function handlePublishToggle(look: LookDto) {
    if (confirmPublishId !== look.id) {
      setConfirmPublishId(look.id);
      return;
    }
    setConfirmPublishId(null);
    try {
      if (look.isPublished) await unpublishLook.mutateAsync(look.id);
      else await publishLook.mutateAsync(look.id);
    } catch (err) {
      setToast(friendlyError(err));
    }
  }

  function handleAppeal(look: LookDto) {
    if (appeal.isPending || appealedIds.has(look.id)) return;
    appeal.mutate(
      { subjectKind: 'look', subjectId: String(look.id) },
      { onSuccess: () => setAppealedIds((prev) => new Set(prev).add(look.id)) },
    );
  }

  async function handleDelete(look: LookDto) {
    if (confirmDeleteId !== look.id) {
      setConfirmDeleteId(look.id);
      return;
    }
    try {
      await deleteLook.mutateAsync(look.id);
      setConfirmDeleteId(null);
    } catch (err) {
      setToast(friendlyError(err));
    }
  }

  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="looks-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
      variant={variant}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>Looks</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close Looks sheet">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Your saved Looks for this companion — tap one to wear it, or manage it below.
      </Dialog.Description>

      <div className="sheet-body">
        {looks.length === 0 ? (
          /* "Save your first Look from the wardrobe" — read from inside the wardrobe, with the
              outfit already on the pet, that is an instruction to go where you are. When the host
              can actually save (the wardrobe passes `onSaveCurrent`), the sentence names what the
              button does instead; from Habitat it stays as it was, because there it is true. */
          onSaveCurrent ? (
            <div className="looks-empty">
              <p className="form-note">No Looks saved yet.</p>
              <button type="button" className="btn-primary" onClick={onSaveCurrent}>
                Save this Look
              </button>
            </div>
          ) : (
            <p className="form-note">Save your first Look from the wardrobe</p>
          )
        ) : (
          <ul className="looks-list">
            {looks.map((look) => {
              const isEditing = editingId === look.id;
              return (
                <li key={look.id} className="looks-row">
                  <button
                    type="button"
                    className="looks-apply"
                    aria-label={`Wear ${look.name}`}
                    disabled={putOutfit.isPending}
                    onClick={() => handleApply(look)}
                  >
                    <LookPicture
                      species={look.speciesCode}
                      stage={look.stage}
                      worn={look.worn}
                      alt={look.name}
                      className="looks-thumb"
                      width={THUMB_SIZE}
                      height={THUMB_SIZE}
                    />
                  </button>

                  <div className="looks-info">
                    {isEditing ? (
                      <form className="looks-rename-form" onSubmit={(e) => submitRename(e, look)}>
                        <input
                          aria-label="Look name"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => setEditTouched(true)}
                          disabled={patchLook.isPending}
                        />
                        {nameError && <p className="field-error" role="alert">{nameError}</p>}
                        {!nameError && renameFormError && (
                          <p className="field-error" role="alert">{renameFormError}</p>
                        )}
                        <div className="looks-rename-actions">
                          <button type="submit" className="btn-quiet" disabled={patchLook.isPending}>
                            Save
                          </button>
                          <button type="button" className="btn-quiet" onClick={cancelRename}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <span className="looks-name">{look.name}</span>
                        <div className="looks-badges">
                          {look.isPublished && (
                            <span className="looks-badge">
                              <span aria-hidden="true">★</span> Published
                            </span>
                          )}
                          {look.isProfileDisplay && <span className="looks-badge">Profile</span>}
                        </div>
                        {/* Task 19 (R4, "owner sees own content flagged, honest, never silent"):
                            own-hidden-content state — this look was shadow-hidden by moderation
                            (directly, or via a profile-level hide cascading down, per
                            LookService#toDto's own javadoc) and is now invisible to everyone but
                            its owner, here, in the gallery, and on the profile page. */}
                        {look.hidden && (
                          <div className="looks-moderation-notice">
                            <p className="form-note" role="alert">
                              Hidden pending review — {reasonLabel(look.moderationReason)}
                            </p>
                            <button
                              type="button"
                              className="btn-quiet"
                              disabled={appeal.isPending || appealedIds.has(look.id)}
                              onClick={() => handleAppeal(look)}
                            >
                              {appealedIds.has(look.id) ? 'Appeal requested' : 'Appeal'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {!isEditing && (
                    <div className="looks-actions">
                      <button type="button" className="btn-quiet" onClick={() => startRename(look)}>
                        Rename
                      </button>
                      <button type="button" className="btn-quiet" onClick={() => toggleProfileDisplay(look)}>
                        {look.isProfileDisplay ? 'Remove as profile' : 'Set as profile'}
                      </button>
                      <button
                        type="button"
                        className="btn-quiet"
                        disabled={publishLook.isPending || unpublishLook.isPending}
                        onClick={() => handlePublishToggle(look)}
                      >
                        {confirmPublishId === look.id
                          ? (look.isPublished ? 'Confirm unpublish' : 'Confirm publish')
                          : (look.isPublished ? 'Unpublish' : 'Publish')}
                      </button>
                      {confirmPublishId === look.id && !look.isPublished && (
                        <p className="form-note">
                          Published Looks appear in the public gallery with your display name.
                        </p>
                      )}
                      <button
                        type="button"
                        className="btn-destructive-quiet"
                        disabled={deleteLook.isPending}
                        onClick={() => handleDelete(look)}
                      >
                        {confirmDeleteId === look.id ? 'Confirm delete' : 'Delete'}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {toast && (
          <div className="toast" role="alert">
            <span>{toast}</span>
            <button type="button" className="toast-dismiss" onClick={() => setToast(null)}>Dismiss</button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
